import { persistentStorage } from './storage';

export const SESSION_ISSUED_AT_KEY = 'sessionIssuedAt';
export const SESSION_EXPIRES_AT_KEY = 'sessionExpiresAt';
// Persistent login: 30 days. Closing/reopening keeps the user signed in;
// they only re-authenticate after 30 days without a login.
export const DEFAULT_SESSION_TTL_MINUTES = 30 * 24 * 60; // 43200

export async function markSessionStart(ttlMinutes = DEFAULT_SESSION_TTL_MINUTES) {
  const nowMs = Date.now();
  const ttlMs = ttlMinutes * 60 * 1000;
  const expiresAt = nowMs + ttlMs;
  try {
    await persistentStorage.set(SESSION_ISSUED_AT_KEY, String(nowMs));
    await persistentStorage.set(SESSION_EXPIRES_AT_KEY, String(expiresAt));
  } catch (e) {
    // Fallback to sync for web
    try {
      localStorage.setItem(SESSION_ISSUED_AT_KEY, String(nowMs));
      localStorage.setItem(SESSION_EXPIRES_AT_KEY, String(expiresAt));
    } catch (_) {}
  }
}

export async function isSessionValid() {
  try {
    const expiresAtStr = await persistentStorage.get(SESSION_EXPIRES_AT_KEY);
    if (expiresAtStr) {
      const expiresAt = Number(expiresAtStr);
      if (!Number.isNaN(expiresAt) && Date.now() < expiresAt) return true;
      return false;
    }
    // Fallback: check issuedAt with default TTL
    const issuedAtStr = await persistentStorage.get(SESSION_ISSUED_AT_KEY);
    if (issuedAtStr) {
      const issuedAt = Number(issuedAtStr);
      if (!Number.isNaN(issuedAt)) {
        const ageMs = Date.now() - issuedAt;
        return ageMs < DEFAULT_SESSION_TTL_MINUTES * 60 * 1000;
      }
    }
  } catch (e) {
    // Fallback to localStorage on web
    try {
      const expiresAtStr = localStorage.getItem(SESSION_EXPIRES_AT_KEY);
      if (expiresAtStr) {
        const expiresAt = Number(expiresAtStr);
        if (!Number.isNaN(expiresAt) && Date.now() < expiresAt) return true;
        return false;
      }
      const issuedAtStr = localStorage.getItem(SESSION_ISSUED_AT_KEY);
      if (issuedAtStr) {
        const issuedAt = Number(issuedAtStr);
        if (!Number.isNaN(issuedAt)) {
          const ageMs = Date.now() - issuedAt;
          return ageMs < DEFAULT_SESSION_TTL_MINUTES * 60 * 1000;
        }
      }
    } catch (_) {}
  }
  return false;
}

export async function clearSessionTimestamps() {
  try {
    await persistentStorage.remove(SESSION_ISSUED_AT_KEY);
    await persistentStorage.remove(SESSION_EXPIRES_AT_KEY);
  } catch (e) {
    try {
      localStorage.removeItem(SESSION_ISSUED_AT_KEY);
      localStorage.removeItem(SESSION_EXPIRES_AT_KEY);
    } catch (_) {}
  }
}


/**
 * Full local-session teardown on logout.
 *
 * `localStorage.clear()` alone is NOT enough on the native builds: identity and
 * session keys are written through `persistentStorage` (Capacitor Preferences),
 * which survives an app relaunch, and `persistentStorage.hydrate()` copies them
 * straight back into localStorage at boot. A logout that only cleared
 * localStorage therefore left the user auto-restored into their dashboard on the
 * next launch WITHOUT going through AssociatesLogin — so `/user-status/login`
 * was never posted and no user_status row was ever opened. The valet kept
 * working while the manager/TOS dashboards showed him OFF DUTY, and logging out
 * and back in could not fix it because the logout was what created the state.
 *
 * Clearing Preferences is what actually ends the session; the localStorage
 * clear below is the web fallback for when Preferences is unavailable.
 */
export async function clearLocalSession() {
  try {
    await persistentStorage.clear();
  } catch (e) {
    console.warn('[session] persistentStorage.clear failed, falling back:', e);
  }
  try {
    localStorage.clear();
  } catch (_) { /* native WebView availability */ }
}
